import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, MapPin, X } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Inline,
  Stack,
  Text,
} from '@relay-towing/ui';
import { useInspectionReport } from '@/hooks/useTruckInspections';
import { type ReportAnswer } from '@/api/truckInspections';
import { answerValue, statusLabel, whenLabel } from '@/lib/inspections';
import { AppBottomNav } from '@/components/navigation/AppBottomNav';
import { ReportScreenSkeleton } from './components/InspectionSkeletons';

/** The card the run screen builds its blocks out of. Same look here. */
const CARD = 'rounded-xl border border-border bg-card p-4';

/** A finished report, read only. What was actually sent. */
export default function InspectionReportScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  // The shared hook, not a second copy of the query. Two copies meant two
  // cache keys for one report, and only one of them ever got invalidated.
  const { data: report, isPending, isError, refetch } = useInspectionReport(id);

  if (isPending) {
    return <ReportScreenSkeleton onBack={() => navigate('/inspections')} />;
  }

  if (isError || !report) {
    // This used to sit on the skeleton forever, which reads as a slow network
    // rather than a request that already failed.
    return (
      <div className="min-h-screen bg-background pb-20">
        <header className="sticky top-0 z-10 border-b border-border bg-card px-4 py-3">
          <Button
            variant="link"
            size="inline"
            onClick={() => navigate('/inspections')}
          >
            <ArrowLeft />
            Back
          </Button>
        </header>
        <div className="p-4">
          <EmptyState
            icon={AlertTriangle}
            title="Could not open this report"
            description="Check your signal and try again."
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        </div>
        <AppBottomNav activeTab="inspections" />
      </div>
    );
  }

  const sections: [string, ReportAnswer[]][] = [];
  report.answers.forEach((answer) => {
    const key = answer.itemSection || 'Checklist';
    const found = sections.find(([name]) => name === key);
    if (found) found[1].push(answer);
    else sections.push([key, [answer]]);
  });

  const failed = report.answers.filter((a) => a.status === 'FAIL');
  const issues = report.status === 'ISSUES_FOUND';
  const flagged = `${failed.length} item${
    failed.length === 1 ? '' : 's'
  } flagged for the shop`;

  const when = whenLabel(report.submittedAt);
  // Photos of the truck itself. The per-answer strips are drawn with their
  // questions; these belong to the report and have nowhere else to go.
  const truckPhotos = report.photoUrls ?? [];
  const signed = Boolean(report.signatureUrl || report.signedName);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 border-b border-border bg-card px-4 py-3">
        <Button
          variant="link"
          size="inline"
          onClick={() => navigate('/inspections')}
        >
          <ArrowLeft />
          Inspections
        </Button>
      </header>

      <Stack gap={4} className="p-4">
        {/* The run screen's summary card, read back: what was checked, what
            was run on it, how it came out, and the odometer under the rule. */}
        <Stack gap={3} className="rounded-2xl border border-border bg-card p-4">
          <Inline gap={3} align="start" justify="between">
            <Stack gap={0} className="min-w-0">
              <Text as="h1" variant="h2">
                {report.subjectLabel}
              </Text>
              <Text variant="body-sm" tone="muted">
                {report.templateName}
                {when ? ` · ${when}` : ''}
              </Text>
            </Stack>
            <Badge
              tone={
                issues
                  ? 'destructive'
                  : report.status === 'PASSED'
                    ? 'success'
                    : 'neutral'
              }
              className="shrink-0"
            >
              {statusLabel(report.status)}
            </Badge>
          </Inline>

          {report.odometer != null && (
            <Inline
              gap={1}
              align="center"
              className="border-t border-border pt-3"
            >
              <Text variant="caption" tone="muted">
                Odometer
              </Text>
              <Text variant="caption" tone="muted" numeric>
                {report.odometer.toLocaleString()}
              </Text>
            </Inline>
          )}
        </Stack>

        {/*
          The tone follows the report's status, not the count of failed
          answers. A question can be set so that failing it is written down
          without failing the truck, and the banner has to agree with the
          status the office sees.
        */}
        <div
          className={[
            'flex items-center gap-2 rounded-xl p-4',
            issues
              ? 'bg-destructive-subtle text-destructive-subtle-foreground'
              : 'bg-success-subtle text-success-subtle-foreground',
          ].join(' ')}
        >
          {issues ? <AlertTriangle /> : <Check />}
          <Text variant="caption" weight="medium">
            {issues
              ? flagged
              : failed.length === 0
                ? 'Everything passed'
                : `Passed. ${flagged}`}
          </Text>
        </div>

        {sections.map(([section, answers]) => (
          <Stack key={section} gap={3}>
            <Text as="h2" variant="body-sm-strong" className="px-1">
              {section}
            </Text>
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              {answers.map((answer, index) => (
                <Stack
                  key={answer.id}
                  gap={2}
                  className={[
                    'p-4',
                    index > 0 ? 'border-t border-border' : '',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <Stack gap={0} className="min-w-0 flex-1">
                      <Text variant="caption" weight="medium">
                        {answer.itemLabel}
                      </Text>
                      {answer.note && (
                        <Text variant="caption" tone="muted">
                          {answer.note}
                        </Text>
                      )}
                    </Stack>
                    <span
                      className={[
                        'flex shrink-0 items-center gap-1',
                        answer.status === 'FAIL'
                          ? 'text-destructive'
                          : answer.status === 'PASS'
                            ? 'text-success'
                            : 'text-muted-foreground',
                      ].join(' ')}
                    >
                      {answer.status === 'FAIL' && (
                        <X className="size-icon-sm" />
                      )}
                      {answer.status === 'PASS' && (
                        <Check className="size-icon-sm" />
                      )}
                      <Text variant="caption" weight="medium">
                        {answerValue(answer)}
                      </Text>
                    </span>
                  </div>
                  {answer.photoUrls.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto">
                      {answer.photoUrls.map((url) => (
                        <img
                          key={url}
                          src={url}
                          alt=""
                          className="h-16 w-16 shrink-0 rounded-lg object-cover"
                        />
                      ))}
                    </div>
                  )}
                </Stack>
              ))}
            </div>
          </Stack>
        ))}

        {report.notes && (
          <Stack gap={2} className={CARD}>
            <Text as="h2" variant="body-sm-strong">
              Notes
            </Text>
            <Text variant="caption" tone="muted" className="text-pretty">
              {report.notes}
            </Text>
          </Stack>
        )}

        {truckPhotos.length > 0 && (
          <Stack gap={3} className={CARD}>
            <Text as="h2" variant="body-sm-strong">
              Photos
            </Text>
            <div className="flex gap-2 overflow-x-auto">
              {truckPhotos.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                />
              ))}
            </div>
          </Stack>
        )}

        {/* Only the label. A pair of coordinates tells a driver nothing they
            could read off a screen, so without one there is no line. */}
        {report.locationLabel && (
          <Inline gap={1} align="center" className="px-1">
            <MapPin className="size-icon-xs shrink-0 text-muted-foreground" />
            <Text variant="caption" tone="muted" className="min-w-0">
              {report.locationLabel}
            </Text>
          </Inline>
        )}

        {signed && (
          <Stack gap={3} className={CARD}>
            <Text as="h2" variant="body-sm-strong">
              Signed off
            </Text>
            {report.signatureUrl && (
              <img
                src={report.signatureUrl}
                alt="Signature"
                className="max-h-24 w-full rounded-lg bg-muted object-contain p-2"
              />
            )}
            {report.signedName && (
              <Text variant="caption" tone="muted">
                Signed by {report.signedName}
              </Text>
            )}
          </Stack>
        )}
      </Stack>

      <AppBottomNav activeTab="inspections" />
    </div>
  );
}
